import { Dist } from "./Dist"
import { appdata, memberKind } from "./internalModules"
import { WallType } from "./internalModules"
import {AssemblyHorizontal} from "./internalModules"
import Graphics from "./Graphics"
import * as THREE from 'three'
import {Material} from "./internalModules"
import {AMember} from "./internalModules"
import { mapMerge } from "./utility"
import { v4 as uuidv4 } from 'uuid'

export type ALayerInfo = {
  kind: string,
  memberGuid: string,
  materialGuid: string,
  thickness: number, //inches
  fillMaterialGuid: string,
  fillThickness: number, //inches
  framedVSpacing: number, //inches
  framedHSpacing: number, //inches
  framedMembersTop: number,
  framedMembersBottom: number,
  framedMembersLeft: number,
  framedMembersRight: number,
  framingDirection: string,
  framingSpacing: number, //inches
  columnHeightRef: number, // 0, 1, 2, 3
  columnHeightDim: number, //inches
  notes: string, // optional user notes
}

export const defaultALayerInfo: ALayerInfo = {
  kind: '',
  memberGuid: '',
  materialGuid: '0',
  thickness: 0, //inches
  fillMaterialGuid: '',
  fillThickness: 0, //inches
  framedVSpacing: 0, //inches
  framedHSpacing: 0, //inches
  framedMembersTop: 0,
  framedMembersBottom: 0,
  framedMembersLeft: 0,
  framedMembersRight: 0,
  framingDirection: 'length',
  framingSpacing: 0, //inches
  columnHeightRef: 3, // 0, 1, 2, 3
  columnHeightDim: 0, //inches
  notes: '',
}

export enum buckets {
  wall = 'Wall',
  glazed = 'Glazed',
  hSolid = 'HorizontalSolid',
  hFraming = 'HorizontalFraming',
  column = 'Column',
}

export enum kind {
  solid = 'solid',
  framed = 'framed',
  framing = 'framing',
  column = 'column',
  mDeck = 'mDeck'
}

export type CalcRow = {
  layerGwp: number,
  layerGwpSf: number,
  layerNumber: string,
  materialDensity: number,
  materialGwp: number,
  materialGwpUnits: string,
  materialName: string,
  materialQty: number,
  materialQtyUnits: string,
  memberArea: number,
  memberLength: number,
  memberSectionArea: number,
  memberWidth: number,
  moduleLength: number,
  moduleWidth: number,
  thickness: number,
}

export class ALayer {
  id: string
  kind: string
  memberGuid: string
  materialGuid: string
  thickness: Dist
  fillMaterialGuid: string
  fillThickness: Dist 
  framedVSpacing: Dist
  framedHSpacing: Dist
  framedMembersTop: number
  framedMembersBottom: number
  framedMembersLeft: number
  framedMembersRight: number
  framingDirection: string // length or width
  framingSpacing: Dist
  columnHeightRef: number // 0, 1, 2, 3
  columnHeightDim: Dist
  notes: string
  calcRows: CalcRow[]

  constructor (info: ALayerInfo = defaultALayerInfo) {
    this.id = uuidv4() //for draggable context only
    this.kind = info.kind
    this.memberGuid = info.memberGuid
    this.materialGuid = info.materialGuid
    this.thickness = Dist.inches(info.thickness)
    this.fillMaterialGuid = info.fillMaterialGuid
    this.fillThickness = Dist.inches(info.fillThickness)
    this.framedVSpacing = Dist.inches(info.framedVSpacing)
    this.framedHSpacing = Dist.inches(info.framedHSpacing)
    this.framedMembersTop = info.framedMembersTop
    this.framedMembersBottom = info.framedMembersBottom
    this.framedMembersLeft = info.framedMembersLeft
    this.framedMembersRight = info.framedMembersRight
    this.framingDirection = info.framingDirection
    this.framingSpacing = info.framingSpacing > 0
      ? Dist.inches(info.framingSpacing) : Dist.zero
    this.columnHeightRef = info.columnHeightRef
    this.columnHeightDim = info.columnHeightDim > 0 
      ? Dist.inches(info.columnHeightDim) : Dist.zero
    this.notes = info.notes
    this.calcRows = []
  }

  get info(): ALayerInfo {
    let info: ALayerInfo = {
      kind: this.kind,
      memberGuid: this.memberGuid,
      materialGuid: this.materialGuid,
      thickness: this.thickness.in,
      fillMaterialGuid: this.fillMaterialGuid,
      fillThickness: this.fillThickness.in,
      framedVSpacing: this.framedVSpacing.in,
      framedHSpacing: this.framedHSpacing.in,
      framedMembersTop: this.framedMembersTop,
      framedMembersBottom: this.framedMembersBottom,
      framedMembersLeft: this.framedMembersLeft,
      framedMembersRight: this.framedMembersRight,
      framingDirection: this.framingDirection,
      framingSpacing: this.framingSpacing.in,
      columnHeightRef: this.columnHeightRef,
      columnHeightDim: this.columnHeightDim.in,
      notes: this.notes
    }
    return info
  }

  get category(): string {
    let matGuid = this.materialGuid
    if(this.kind !== kind.solid){
      matGuid = appdata.getMember(this.memberGuid).materialGuid
    }
    return appdata.getMaterial(matGuid).category
  }

  get totalThickness(): Dist {
    const member = appdata.getMember(this.memberGuid)
    if (this.kind === kind.solid){
      return this.thickness
    }
    if (this.kind === kind.framed || this.kind === kind.framing){
      if (this.fillThickness > member.depth){
        return this.fillThickness
      }
      return member.depth
    }
    if (this.kind === kind.mDeck){
      return Dist.feet(member.depth.ft + this.fillThickness.ft)
    }
    return Dist.zero
  }

  name(assembly: WallType): string {
    let n = ''
    switch (this.kind) {
      case kind.solid:
        n = `${appdata.getMaterial(this.materialGuid).name} (${this.thickness.toInchString(2)} thick)`
        break
      case kind.framed:
        n = `${appdata.getMember(this.memberGuid).name} at ${
          this.framedVSpacing.toInchString(2)}O.C. x ${this.framedHSpacing.toInchString(2)}O.C.`
        if (this.fillThickness.in > 0) {
          const fillMat = appdata.getMaterial(this.fillMaterialGuid)
          n += ` (Infill: ${this.fillThickness.toInchString(2)} ${fillMat.name})`
        }
        if(this.framedMembersTop + this.framedMembersBottom 
          + this.framedMembersLeft + this.framedMembersRight > 0){
            n += ` Edge Members`
            if(this.framedMembersTop > 0){
              n += ` Top:${this.framedMembersTop}`
            }
            if(this.framedMembersBottom > 0){
              n += ` Bot:${this.framedMembersBottom}`
            }
            if(this.framedMembersLeft > 0){
              n += ` L:${this.framedMembersLeft}`
            }
            if(this.framedMembersRight > 0){
              n += ` R:${this.framedMembersRight}`
            }
          }
        break
      case kind.framing:
        let memberLength = Dist.zero
        if(this.framingDirection === 'width'){
          memberLength = assembly.width
        }
        else{
          memberLength = assembly.length
        }
        n = `${appdata.getMember(this.memberGuid).name}, ${memberLength.toFeetString(2)} long spanning module ${this.framingDirection}`
        if(this.framingSpacing.ft > 0){
          n += ` at ${this.framingSpacing.toInchString(2)} O.C.`
        }
        break
      case kind.column:
        if(assembly instanceof AssemblyHorizontal){
          let colHeight = assembly.getColumnHeight(this)
          if(!colHeight){
            colHeight = Dist.zero
          }

          n = `${appdata.getMember(this.memberGuid).name}, ${colHeight.toFeetString(2)} tall`
          switch (this.columnHeightRef) {
            case 0: n += ' by Custom height'    
              break
            case 1: n += ' to Bottom of Framing'
              break
            case 2: n += ' to Top of Framing'
              break
            case 3: n += ' to Top of Module'
          }
        }
        break
      case kind.mDeck:
        n = `${appdata.getMember(this.memberGuid).name}, spanning ${this.framingDirection} direction`
        if(this.fillThickness.in > 0 && !(appdata.getMaterial(this.fillMaterialGuid).name.includes('<none>')) ){
          n += ` with ${this.fillThickness.toInchString(2)} of ${appdata.getMaterial(this.fillMaterialGuid).name}`
        }
        break
      default:
        break
    }
    return n
  }

  /**Get the Material-GWP map for this layer in the specified assembly*/
  getMaterialGwpMap(assembly: WallType): Map<string, number>{
    let map = new Map<string, number>()
    let gwp = 0
    let mat: Material
    const member = appdata.getMember(this.memberGuid)
    switch (this.kind) {
      case kind.solid:
        mat = appdata.getMaterial(this.materialGuid)
        let dimx = assembly.width.ft
        let dimy = assembly.height.ft
        if(assembly instanceof AssemblyHorizontal){
          dimy = assembly.length.ft
        }
        //console.log(`WxH = ${dimx} x ${dimy}`)
        if (mat.gwpByArea > 0) {
          gwp = mat.gwpByArea * dimy * dimx
          //console.log('gwp by area = ', gwp, mat.gwpByArea, dimy, dimx)
        }
        if (mat.gwpByWeight > 0) {
          gwp = mat.gwpByWeight * dimy * dimx * this.thickness.ft * mat.density
          //console.log('gwp by weight = ', gwp, mat.gwpByWeight, dimy, dimx, this.thickness.ft, mat.density)
        }
        if (mat.gwpByVolume > 0) {
          gwp = mat.gwpByVolume * dimy * dimx * this.thickness.ft
          //console.log(`gwp by volume = ${mat.gwpByVolume}kgCO2e/ft3 x ${dimy}ft x ${dimx}ft x ${this.thickness.ft}ft`)
        }
        map.set(mat.name, gwp)
        break
      case kind.framed:
        let wtypeH = assembly.height.value
        let wtypeW = assembly.width.value
        mat = appdata.getMaterial(member.materialGuid)
        // calculate length of all members
        let totalLength = 0
        // HORIZONTAL MEMBERS
        //for horizontal members length will be walltype width
        let len = assembly.width.ft
        //add the bottom and top members
        for (let i = 0; i < this.framedMembersTop + this.framedMembersBottom; i++) {
          totalLength += len
        }
        //add the intermediate horizontal members
        let remainder = wtypeH - (this.framedMembersTop + this.framedMembersBottom) * member.width.ft;
        while (this.framedHSpacing.ft > 0 && remainder > this.framedHSpacing.ft) {
          totalLength += len
          remainder -= this.framedHSpacing.ft;
        }

        //VERTICAL MEMBERS
        //for vertical members length will be walltype height - member width * (#top + #bot)
        len = wtypeH - (this.framedMembersTop + this.framedMembersBottom) * member.width.ft
        //add the left and right members
        for (let i = 0; i < this.framedMembersLeft + this.framedMembersRight; i++) {
          totalLength += len
        }
        //add the intermediate vertical members
        remainder = wtypeW - (this.framedMembersLeft + this.framedMembersRight) * member.width.ft
        while (this.framedVSpacing.ft > 0 && remainder > this.framedVSpacing.ft) {
          totalLength += len
          remainder -= this.framedVSpacing.ft
        }
        // multiply by cross section area(in2) of member
        const volumeMembers = (member.area /144) * totalLength
        if (mat.gwpByWeight > 0) {
          gwp = mat.gwpByWeight * volumeMembers * mat.density
        }
        if (mat.gwpByVolume > 0) {
          gwp = mat.gwpByVolume * volumeMembers
        }
        map.set(mat.name, gwp)

        // Calculate fill gwp
        if (this.fillThickness.ft === 0) {
          break
        }
        // to calculate the gwp of fill layer
        // we subtract the area of members
        // from total module area, this gives area of fill
        const fillMat = appdata.getMaterial(this.fillMaterialGuid)
        let fillGwp = 0
        let fillArea = (assembly.width.ft * assembly.height.ft) - (totalLength * member.width.ft)
        if (fillMat.gwpByArea > 0) {
          fillGwp = fillMat.gwpByArea * fillArea
        }
        if (fillMat.gwpByWeight > 0) {
          fillGwp = fillMat.gwpByWeight * fillArea * this.fillThickness.ft * fillMat.density
        }
        if (fillMat.gwpByVolume > 0) {
          fillGwp = fillMat.gwpByVolume * fillArea * this.fillThickness.ft
        }
        const fillMap = new Map<string, number>()
        fillMap.set(fillMat.name, fillGwp)
        mapMerge(map, fillMap)
        break
      case kind.framing:
        if(assembly instanceof AssemblyHorizontal &&
            (this.framingDirection === 'length' || this.framingDirection === 'width')){
          const memberLength = assembly[this.framingDirection]
          const spacingDist = this.framingDirection === 'length' ? assembly['width'] : assembly['length']
          let memberCount = 1 // assume half member at each edge of assembly = 1 member
          let remainder = spacingDist.ft
          while(this.framingSpacing.ft > 0 && remainder > this.framingSpacing.ft){
            memberCount++
            remainder -= this.framingSpacing.ft
          }
          mat = appdata.getMaterial(member.materialGuid)
          let gwpSingle = 0 //gwp of a single member
          if (mat.gwpByWeight > 0) {
            gwpSingle = mat.gwpByWeight * (member.area/144) * memberLength.ft * mat.density
          }
          if (mat.gwpByVolume > 0) {
            gwpSingle = mat.gwpByVolume * (member.area/144) * memberLength.ft 
          }
          const gwpAll = gwpSingle * memberCount
          //console.log(`${gwpSingle.toFixed(1)}single x ${memberCount} = ${gwpAll.toFixed(2)}`)
          map.set(mat.name, gwpAll)
        }
        break
      case kind.column:
        if(assembly instanceof AssemblyHorizontal){
          let columnHeight = assembly.getColumnHeight(this).ft
          mat = appdata.getMaterial(member.materialGuid)
          let gwp = 0
          if (mat.gwpByWeight > 0) {
            gwp = mat.gwpByWeight * (member.area/144) * columnHeight * mat.density
          }
          if (mat.gwpByVolume > 0) {
            gwp = mat.gwpByVolume * (member.area/144) * columnHeight
          }
          if (mat.gwpByArea > 0) {
            alert('Warning: member material gwp defined by area will be ignored')
            //TODO: do not allow members with gwp by area
          }
          map.set(mat.name, gwp)
        }
        break
      case kind.mDeck:
        if(member.kind === memberKind.metalDeck && assembly instanceof AssemblyHorizontal
        && (this.framingDirection === 'length' || this.framingDirection === 'width')){
          const spanLength = assembly[this.framingDirection]
          const crossSpanWidth = this.framingDirection === 'length' ? assembly['width'] : assembly['length']
          mat = appdata.getMaterial(member.materialGuid)
          if (mat.gwpByWeight > 0) {
            gwp = mat.gwpByWeight * (member.area / 144) * crossSpanWidth.ft * spanLength.ft * mat.density
          }
          if (mat.gwpByVolume > 0) {
            gwp = mat.gwpByVolume * (member.area / 144) * crossSpanWidth.ft * spanLength.ft
          }
          map.set(mat.name, gwp)

          // calculate deck fill material
          mat = appdata.getMaterial(this.fillMaterialGuid)
          if (mat.gwpByVolume + mat.gwpByWeight + mat.gwpByWeight > 0 && mat.name !== '<none>') {
            const fluteFillVolume = (member.fillArea / 144) * crossSpanWidth.ft * spanLength.ft
            const toppingVolume = crossSpanWidth.ft * spanLength.ft * this.fillThickness.ft
            if (mat.gwpByWeight > 0) {
              gwp = mat.gwpByWeight * (fluteFillVolume + toppingVolume) * mat.density
            }
            if (mat.gwpByVolume > 0) {
              gwp = mat.gwpByVolume * (fluteFillVolume + toppingVolume)
            }
            const fillMap = new Map<string, number>()
            fillMap.set(mat.name, gwp)
            mapMerge(map, fillMap)
          }
        }
        break
      default:
        break
    }
    return map
  }

  /**Get the mesh geometry for this layer in the specified assembly */
  getMesh(assembly: WallType): THREE.Mesh{
    let geometry = new THREE.Mesh()
    let material = Material.getEmpty()
    let member = AMember.getEmpty()
    if(this.kind === kind.solid){
      material = appdata.getMaterial(this.materialGuid)
    }
    else{
      member = appdata.getMember(this.memberGuid)
      material = appdata.getMaterial(member.materialGuid)
    }
    let dimx = assembly.width.ft
    let dimy = assembly.height.ft
    let dimz = assembly.width.ft //used by Framing and Metal Deck
    if(assembly instanceof AssemblyHorizontal){
      dimy = assembly.length.ft
    }
    let rotation = 0 //used by Framing and Metal Deck
    switch (this.kind) {
      case kind.solid:
        geometry = Graphics.edgeMesh(
          new THREE.BoxGeometry(dimx, dimy, this.thickness.ft),
          material.opacity
        )
        geometry.name = material.name
        break
      case kind.framed:
        //create horizontal edge members
        let len = assembly.width.ft
        if (this.framedMembersBottom > 0) {
          for (let i = 0; i < this.framedMembersBottom; i++) {
            let memberH = Graphics.edgeMesh(new THREE.ExtrudeGeometry(member.shape, {depth: len, bevelEnabled: false}), material.opacity)
            memberH.rotateZ(Math.PI / 2)
            memberH.rotateX(Math.PI / 2)
            memberH.translateX(-assembly.height.ft / 2 + i * member.width.ft)
            memberH.translateY(-member.depth.ft / 2)
            memberH.translateZ(-assembly.width.ft / 2)
            memberH.name = material.name
            geometry.add(memberH)
          }
        }
        if (this.framedMembersTop > 0) {
          for (let i = 0; i < this.framedMembersTop; i++) {
            let memberH = Graphics.edgeMesh(new THREE.ExtrudeGeometry(member.shape, {depth: len, bevelEnabled: false}), material.opacity)
            memberH.rotateZ(-Math.PI / 2)
            memberH.rotateX(-Math.PI / 2)
            memberH.translateY(-member.depth.ft / 2)
            memberH.translateZ(-assembly.width.ft / 2)
            memberH.translateX(-assembly.height.ft / 2 + i*member.width.ft)
            memberH.name = material.name
            geometry.add(memberH)
          }
        }
        //create horizontal interior members
        if (this.framedHSpacing.ft > 0) {
          let remainder = assembly.height.ft - (this.framedMembersTop + this.framedMembersBottom) * member.width.ft
          let position = this.framedHSpacing.ft
          while (remainder > this.framedHSpacing.ft) {
            let memberH = Graphics.edgeMesh(new THREE.ExtrudeGeometry(member.shape, {depth: len, bevelEnabled: false}), material.opacity)
            memberH.rotateZ(-Math.PI / 2)
            memberH.rotateX(-Math.PI / 2)
            memberH.translateY(-member.depth.ft / 2)
            memberH.translateZ(-assembly.width.ft / 2)
            memberH.translateX(assembly.height.ft / 2 - (this.framedMembersBottom * member.width.ft + position) )
            memberH.name = material.name
            geometry.add(memberH)
            remainder -= this.framedHSpacing.ft
            position += this.framedHSpacing.ft
          }
        }
        //create vertical edge members
        len = assembly.height.ft - (member.width.ft * (this.framedMembersBottom + this.framedMembersTop) )
        if (this.framedMembersLeft > 0) {
          for (let i = 0; i < this.framedMembersLeft; i++) {
            let memberV = Graphics.edgeMesh(new THREE.ExtrudeGeometry(member.shape, {depth: len, bevelEnabled: false}), material.opacity)
            memberV.rotateX(-Math.PI / 2)
            memberV.translateY(-member.depth.ft / 2)
            memberV.translateZ(-assembly.height.ft / 2 + (this.framedMembersBottom * member.width.ft) )
            memberV.translateX(-assembly.width.ft / 2 + i * member.width.ft)
            memberV.name = material.name
            geometry.add(memberV)
          }
        }
        if (this.framedMembersRight > 0) {
          for (let i = 0; i < this.framedMembersRight; i++) {
            let memberV = Graphics.edgeMesh(new THREE.ExtrudeGeometry(member.shape, {depth: len, bevelEnabled: false}), material.opacity)
            memberV.rotateX(-Math.PI / 2)
            memberV.translateY(-member.depth.ft / 2)
            memberV.translateZ(-assembly.height.ft / 2 + (this.framedMembersBottom * member.width.ft) )
            memberV.translateX(assembly.width.ft / 2 - (i+1) * member.width.ft)
            memberV.name = material.name
            geometry.add(memberV)
          }
        }
        //create vertical interior members
        if (this.framedVSpacing.ft > 0) {
          let remainder = assembly.width.ft - (this.framedMembersLeft + this.framedMembersRight) * member.width.ft
          let position = this.framedVSpacing.ft - member.width.ft
          while (remainder > this.framedVSpacing.ft) {
            let memberV = Graphics.edgeMesh(new THREE.ExtrudeGeometry(member.shape, {depth: len, bevelEnabled: false}), material.opacity)
            memberV.rotateX(-Math.PI / 2)
            memberV.translateY(-member.depth.ft / 2)
            memberV.translateZ(-assembly.height.ft / 2 + (this.framedMembersBottom * member.width.ft) )
            memberV.translateX(-assembly.width.ft / 2 + (this.framedMembersLeft * member.width.ft + position))
            memberV.name = material.name
            geometry.add(memberV)
            remainder -= this.framedVSpacing.ft
            position += this.framedVSpacing.ft
          }
        }
        //create solid fill geometry
        if (this.fillThickness.ft > 0) {
          let explodeAmount = 0
          if (assembly.exploded) {
            explodeAmount = Graphics.explodeFillAmount
          }
          const vsp = this.framedVSpacing.ft
          const hsp = this.framedHSpacing.ft
          const fillMaterial = appdata.getMaterial(this.fillMaterialGuid)
          //vertical solid fill
          if( vsp > 0 && hsp === 0) {
            let remainder = assembly.width.ft - (this.framedMembersLeft + this.framedMembersRight) * member.width.ft
            let position = (vsp - member.width.ft) / 2 //+ this.framedMembersLeft * member.width.ft
            while ( remainder > 0) {
              let fillWidth = vsp - member.width.ft
              if (remainder < fillWidth) {
                fillWidth = remainder
                position = position - vsp/2 + remainder/2 + member.width.ft/2
              }
              let fillV = Graphics.edgeMesh(new THREE.BoxGeometry( fillWidth, len, this.fillThickness.ft), fillMaterial.opacity)
              fillV.translateZ( this.totalThickness.ft / 2 - this.fillThickness.ft / 2 + explodeAmount)
              fillV.translateY( this.framedMembersBottom * member.width.ft - ((this.framedMembersBottom + this.framedMembersTop) * member.width.ft / 2) )
              fillV.translateX( -assembly.width.ft / 2 + (this.framedMembersLeft * member.width.ft) + position )
              fillV.name = fillMaterial.name
              geometry.add(fillV)
              remainder -= vsp
              position += vsp
            }
          }
          //horizontal solid fill
          if ( vsp === 0 && hsp > 0 ) {
            len = assembly.width.ft - (this.framedMembersLeft + this.framedMembersRight) * member.width.ft
            let remainder = assembly.height.ft - (this.framedMembersBottom + this.framedMembersTop) * member.width.ft
            let position = (hsp - member.width.ft) / 2
            while ( remainder > 0 ) {
              let fillWidth = hsp - member.width.ft
              if (remainder < fillWidth) {
                fillWidth = remainder
                position = position - hsp/2 + remainder/2 + member.width.ft/2
              }
              let fillH = Graphics.edgeMesh(new THREE.BoxGeometry( len, fillWidth, this.fillThickness.ft), fillMaterial.opacity)
              fillH.translateZ( this.totalThickness.ft / 2 - this.fillThickness.ft / 2  + explodeAmount)
              fillH.translateX( this.framedMembersLeft * member.width.ft - ((this.framedMembersLeft + this.framedMembersRight) * member.width.ft / 2) )
              fillH.translateY( -assembly.height.ft / 2 + (this.framedMembersBottom * member.width.ft) + position )
              fillH.name = fillMaterial.name
              geometry.add(fillH)
              remainder -= hsp
              position += hsp
            }
          }
          //horizontal and vertical solid fill
          if ( vsp > 0 && hsp > 0 ){
            let remainderH = assembly.height.ft - (this.framedMembersBottom + this.framedMembersTop) * member.width.ft
            //let positionH = (hsp - member.width.ft) / 2
            let positionH = -assembly.height.ft / 2 + hsp/2 + (this.framedMembersBottom * member.width.ft)
            while (remainderH > 0){
              let remainderV = assembly.width.ft - (this.framedMembersLeft + this.framedMembersRight) * member.width.ft
              let positionV = (vsp - member.width.ft) / 2
              let fillHeight = hsp - member.width.ft
              if (remainderH < fillHeight){
                fillHeight = remainderH
                positionH = positionH - hsp/2 + remainderH/2 + member.width.ft/2
              }
              while (remainderV > 0){
                let fillWidth = vsp - member.width.ft
                if (remainderV < fillWidth) {
                  fillWidth = remainderV
                  positionV = positionV - vsp/2 + remainderV/2 + member.width.ft/2
                }
                let fillV = Graphics.edgeMesh(new THREE.BoxGeometry( fillWidth, fillHeight, this.fillThickness.ft), fillMaterial.opacity)
                fillV.translateZ( this.totalThickness.ft / 2 - this.fillThickness.ft / 2 + explodeAmount)
                fillV.translateY( this.framedMembersBottom * member.width.ft - ((this.framedMembersBottom + this.framedMembersTop) * member.width.ft / 2) + positionH )
                fillV.translateX( -assembly.width.ft / 2 + (this.framedMembersLeft * member.width.ft) + positionV )
                fillV.name = fillMaterial.name
                geometry.add(fillV)
                remainderV -= vsp
                positionV += vsp
              }
              remainderH -= hsp
              positionH += hsp
            }
          }
        }
        break
      case kind.framing:
        dimx = assembly.length.ft
        if(this.framingDirection === 'width'){
          rotation = Math.PI/2
        }
        else{
          dimx = assembly.width.ft
          dimz = assembly.length.ft
        }
        let spacing = this.framingSpacing.ft > 0 ? this.framingSpacing.ft : dimx
        for (let offsetx = 0; offsetx < dimx; offsetx += spacing) {
          let mesh = Graphics.edgeMesh(new THREE.ExtrudeGeometry(member.shape, 
            {depth: dimz, bevelEnabled: false}
          ), material.opacity)
          mesh.rotateY(rotation)
          mesh.translateX(-member.width.ft/2 - dimx/2 + offsetx)
          mesh.translateZ(-dimz/2)
          mesh.translateY(-member.depth.ft)
          mesh.name = appdata.getMaterial(member.materialGuid).name
          geometry.add(mesh)
          if(offsetx === 0){ //copy first member to opposite edge of module
            let meshClone = mesh.clone()
            meshClone.translateX(dimx)
            geometry.add(meshClone)
          }
        }
        break
      case kind.column:
        if(assembly instanceof AssemblyHorizontal){
          const h = assembly.getColumnHeight(this).ft
          let mesh = Graphics.edgeMesh(new THREE.ExtrudeGeometry(
            member.shape, {depth: h, bevelEnabled: false}
          ), material.opacity)
          mesh.rotateX(-Math.PI / 2)
          geometry.translateX(-member.width.ft/2)
          geometry.translateZ(member.depth.ft/2)
          mesh.name = appdata.getMaterial(member.materialGuid).name
          geometry.add(mesh)
        }
        break
      case kind.mDeck:
        dimx = assembly.length.ft
        if(this.framingDirection === 'width'){
          rotation = Math.PI/2
        }
        else{
          dimx = assembly.width.ft
          dimz = assembly.length.ft
        }
        let mesh = Graphics.edgeMesh(new THREE.ExtrudeGeometry(
          Graphics.getLayerMetalDeckShape(member, Dist.feet(dimx)),
            {depth: dimz, bevelEnabled: false}
          ), material.opacity)
        mesh.rotateX(-Math.PI/2)
        mesh.rotateY(rotation)
        mesh.translateX(-dimx/2)
        mesh.translateZ(-dimz/2)
        //mesh.translateY(-member.depth.ft/2)
        mesh.translateY(-this.totalThickness.ft/2)
        mesh.name = material.name
        geometry.add(mesh)

        // concrete fill
        const fillMaterial = appdata.getMaterial(this.fillMaterialGuid)
        if (this.fillThickness.ft > 0 && !fillMaterial.isEmpty()) {
          let explodeAmount = 0
          if (assembly.exploded) {
            explodeAmount = Graphics.explodeFillAmount
          }
          let fillMesh = Graphics.edgeMesh(new THREE.ExtrudeGeometry(
            Graphics.getLayerMetalDeckInfillShape(member, this.fillThickness, Dist.feet(dimx)),
              {depth: dimz, bevelEnabled: false}
            ), fillMaterial.opacity)
          fillMesh.name = fillMaterial.name
          fillMesh.rotateX(-Math.PI/2)
          fillMesh.rotateY(rotation)
          fillMesh.translateX(-dimx/2)
          fillMesh.translateZ(-dimz/2)
          fillMesh.translateY(-this.totalThickness.ft/2 + explodeAmount)
          geometry.add(fillMesh)
        }
        break
      default: break
    }
    
    
    return geometry

  }

  toJSON(): object {
    return this.info
  }

}